import React from "react";
import { Plus } from "lucide-react";
import Tooltip from "../tooltip/Tooltip";
import {
  TextInput,
  TextArea,
  Checkbox,
  PhoneInput,
  DateField,
  SegmentedToggle,
  TextEditor,
  Select,
  RadioGroup,
  ArrayText,
  ImageUpload,
  TieredPricingField,
  LinkedItemsPicker,
  PersonOption,
} from "../ui";
import {
  evaluateCondition,
  evaluateDisabledCondition,
  evaluateRequiredCondition,
} from "./schema/evaluateCondition";
import "./_render-input.scss";
import { useGetUsersByOrgIdQuery } from "../../_features/userManagement/api/userApiSlice";
import { useSelector } from "react-redux";
import { useGetAllCompaniesQuery } from "../../_features/company/api/companyApi";
import { useGetAllMarketsByOrgIdQuery } from "../../_features/markets/api/marketApi";
import RoleAsyncSelect from "./RoleAsyncSelect";
import { openModal } from "../../_app/entityModals/store";

/**
 * Legacy form-field rendering bridge - was `utils/form/renderInput.js`.
 * Superseded for new usage by `_components/form/DynamicForm/FormField.jsx`,
 * which still falls back to this for field types it doesn't yet cover.
 * Still 3 real consumers (old-tree `ConfirmationModal`,
 * `_components/contacts/CustomerContactModal`, `DynamicForm/FormField.jsx`)
 * so kept and relocated rather than deleted - light touch otherwise, this
 * is a legacy path being phased out, not core/high-traffic.
 *
 * Package-retirement status (see docs/package-registry.md): all 16
 * original field types are on in-house `_components/ui` primitives now -
 * zero `@simplifyhq/simplify-ui-reusables` imports left in this file.
 * `simplifyUser`/`company`/`market`/`singleSelect`/`multiSelect` moved
 * onto Select's `renderOption` prop (mapping each field's `items`/
 * `isObject`/`valueSelector`/`valueDisplay`/`searchSelector`/`renderItem`
 * shape onto Select's `options`/`renderOption` at this call site - Select
 * itself needed zero identity-model changes, see its own docstring).
 * `radioButton`/`arrayText`/`imageUpload`/`tieredPricing` moved onto new
 * primitives built specifically for this migration (`RadioGroup`/
 * `ArrayText`/`ImageUpload`/`TieredPricingField`). `radioButton`/`arrayText`/
 * `tieredPricing` have zero real callers anywhere in the app today, so
 * those 3 primitives are best-effort mirrors of the old API, not validated
 * against a live production usage - `imageUpload` is the exception: it's
 * live on the Company Create/Edit form (an org-configured schema field, not
 * a hardcoded one, so it won't show up in a grep of that form's own files).
 * `listManager` moved onto the already-existing
 * `LinkedItemsPicker`, which already replaced this exact reusables
 * component for its own (different) real callers.
 */

// Inline-create footer for the generic `singleSelect` case below (see its
// own comment for why this lives here rather than a dedicated `XSelect.jsx`
// like MarketSelect/UsageCompilationSelect). Keyed by the SAME
// `dynamicConfig.referenceType` string useResolveFormSchema.js switches on -
// only these three ever resolve with `isObject: true` from a real
// entity-modal-registry entity, so only these three get a footer; every
// other referenceType (industries, bundles, markets - handled by its own
// hardcoded `case "market"` above, not here - systemFeatures, products,
// addOns, usageCompilations - handled by the dedicated UsageCompilationSelect,
// not this bridge - etc.) renders exactly as before, byte for byte.
// `unwrap` (optional): most of these entity-modal creates resolve with the
// saved record itself (billingUnits, usageSources), matching what
// `selectItems` already holds - but `create_user` resolves with
// `{success, message, user}` (userController.js wraps the saved user under
// a `user` key for its own callers, e.g. UserManagementPage's own success
// handling elsewhere - not something to change just for this picker), so
// this pulls the actual record back out before handing it to handleChange.
const REFERENCE_TYPE_CREATE_ACTIONS = {
  users: { modalTool: "create_user", label: "Add new user", unwrap: (result) => result?.user },
  billingUnits: { modalTool: "create_billing_unit", label: "Add new billing unit" },
  usageSources: { modalTool: "create_usage_source", label: "Add new usage source" },
};

// Helper function to wrap component with tooltip if tooltip text is provided and field is disabled
const withTooltip = (component, tooltipText, isDisabled = false) => {
  if (!tooltipText || tooltipText.trim() === "" || !isDisabled) {
    return component;
  }

  return (
    <Tooltip content={tooltipText} className="render-input-tooltip-anchor">
      {component}
    </Tooltip>
  );
};

const Users = () => {
  const { data: allUsers } = useGetUsersByOrgIdQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );

  const selectedOrg = useSelector((state) => state?.auth?.user?.selectedOrg);

  const filteredUsers = allUsers
    ?.filter(
      (user) =>
        user?.organisations
          ?.find((org) => org?.orgId === selectedOrg)
          ?.status?.toLowerCase() === "active"
    )
    ?.map((user) => ({
      ...user,
      name: `${user?.firstName} ${user?.lastName}`,
    }));

  return filteredUsers;
};

const Companies = (showToast, context = null) => {
  const { data: allCompanies, error: companiesError } = useGetAllCompaniesQuery(
    context ? { context } : {},
    {
      refetchOnMountOrArgChange: true,
    }
  );

  // Show error toast if companies API fails
  React.useEffect(() => {
    if (companiesError && showToast) {
      showToast(
        `${companiesError?.data?.message || "Failed to load companies"}`,
        "error"
      );
    }
  }, [companiesError, showToast]);

  const companiesData = allCompanies?.companies
    ?.filter((company) => {
      const hasValidCompanyName = company?.companyName?.trim()?.length > 0;
      const isIndividual = company?.isIndividual;
      return isIndividual || hasValidCompanyName;
    })
    ?.map((company) => ({
      ...company,
      name: company?.isIndividual
        ? company?.individualName
        : `${company?.companyName} (${company?.companyShortName})`,
    }));

  return companiesData;
};

const Markets = () => {
  const { data: allMarkets } = useGetAllMarketsByOrgIdQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );

  const marketsData = allMarkets
    ?.filter((market) => market?.marketName?.trim()?.length > 0)
    ?.map((market) => ({
      ...market,
      name: market?.marketName,
    }));

  return marketsData;
};

const renderInput = (
  field,
  formData,
  errors,
  handleChange,
  handleBlur,
  setImageFile,
  setShouldDeleteImage,
  pricingTiers,
  setPricingTiers,
  allFields = [], // Add parameter to pass all fields for condition evaluation
  mode = "create", // Add mode parameter for disabled condition evaluation
  showToast = null, // Add showToast parameter
  context = null,
  filters = null, // Add filters object for various filtering (market, status, etc.)
  forceDisabled = false // Caller-level override (e.g. a mutation in flight) - ORed into every case's own isDisabled below
) => {
  // Validation for integer
  const isValidInteger = (value) => /^-?\d*$/.test(value);
  // Validation for decimal
  const isValidDecimal = (value) => /^-?\d*(\.\d*)?$/.test(value);

  // Helper function to get proper value with default fallback
  const getValueWithDefault = (fieldName, defaultValue) => {
    const value = formData[fieldName];
    if (value !== undefined && value !== null) {
      return value;
    }
    return defaultValue || "";
  };

  // Helper function to get proper array value with default fallback
  const getArrayValueWithDefault = (fieldName, defaultValue) => {
    const value = formData[fieldName];
    if (Array.isArray(value) && value.length > 0) {
      return value;
    }
    return defaultValue || [];
  };
  // Handle showCondition logic with improved evaluation
  if (field.showCondition && typeof field.showCondition === "object") {
    const shouldShow = evaluateCondition(
      field.showCondition,
      formData,
      allFields
    );
    if (!shouldShow) {
      return null;
    }
  }

  // Handle legacy showIf function
  if (typeof field.showIf === "function" && !field.showIf(formData)) {
    return null;
  }

  // Evaluate disabled condition
  const isDisabled =
    forceDisabled ||
    evaluateDisabledCondition(field, formData, allFields, mode);

  switch (field.type) {
    case "imageUpload":
      return withTooltip(
        <ImageUpload
          key={field.name}
          onUpload={(file) => {
            setImageFile(file);
            setShouldDeleteImage(false); // Reset deletion flag if a new image is uploaded
          }}
          existingImage={formData[field.name]}
          setShouldDeleteImage={setShouldDeleteImage}
          disabled={isDisabled}
        />,
        field.tooltip,
        isDisabled
      );

    case "text":
      return withTooltip(
        <TextInput
          key={field.name}
          id={field.name}
          label={field.label}
          type={field.type}
          value={getValueWithDefault(field.name, field.defaultValue)}
          error={field.skipBlurValidation ? undefined : errors?.[field.name]}
          placeholder={field.placeholder}
          onChange={(value) => handleChange(field.name, value)}
          onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          disabled={isDisabled}
        />,
        field.tooltip,
        isDisabled
      );
    case "email":
      return withTooltip(
        <TextInput
          key={field.name}
          id={field.name}
          label={field.label}
          type={field.type}
          value={getValueWithDefault(field.name, field.defaultValue)}
          error={field.skipBlurValidation ? undefined : errors?.[field.name]}
          placeholder={field.placeholder}
          onChange={(value) => handleChange(field.name, value)}
          onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          disabled={isDisabled}
        />,
        field.tooltip,
        isDisabled
      );
    case "number":
      return withTooltip(
        <TextInput
          key={field.name}
          id={field.name}
          label={field.label}
          type="text"
          value={getValueWithDefault(field.name, field.defaultValue)}
          error={field.skipBlurValidation ? undefined : errors?.[field.name]}
          placeholder={field.placeholder}
          onChange={(value) => {
            if (isValidInteger(value)) {
              handleChange(field.name, value);
            } else if (showToast) {
              showToast("Please enter a valid number.", "error");
            }
          }}
          onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          disabled={isDisabled}
          inputMode="numeric"
          pattern="\\d*"
        />,
        field.tooltip,
        isDisabled
      );
    case "numberWithDecimal":
      return withTooltip(
        <TextInput
          key={field.name}
          id={field.name}
          label={field.label}
          type="text"
          value={getValueWithDefault(field.name, field.defaultValue)}
          error={field.skipBlurValidation ? undefined : errors?.[field.name]}
          placeholder={field.placeholder}
          onChange={(value) => {
            // Handle leading dot by converting to "0."
            if (value === ".") {
              handleChange(field.name, "0.");
            } else if (isValidDecimal(value)) {
              handleChange(field.name, value);
            } else if (showToast) {
              showToast(
                "Please enter a valid number (digits and at most one dot).",
                "error"
              );
            }
          }}
          onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          disabled={isDisabled}
          inputMode="decimal"
          pattern="\\d*(\\.\\d*)?"
        />,
        field.tooltip,
        isDisabled
      );
    case "arrayText":
      return withTooltip(
        <ArrayText
          key={field.name}
          id={field.name}
          label={field.label}
          placeholder={field.placeholder}
          values={getArrayValueWithDefault(field.name, field.defaultValue)}
          onChange={(updatedValues) => handleChange(field.name, updatedValues)}
          onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          error={field.skipBlurValidation ? undefined : errors?.[field.name]}
          disabled={isDisabled}
        />,
        field.tooltip,
        isDisabled
      );

    case "checkbox":
      return withTooltip(
        <Checkbox
          key={field.name}
          id={field.name}
          label={field.label}
          value={Boolean(formData[field.name])}
          onChange={(value) => handleChange(field.name, value)}
          onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          disabled={isDisabled}
          error={field.skipBlurValidation ? undefined : errors?.[field.name]}
        />,
        field.tooltip,
        isDisabled
      );

    case "url":
      return withTooltip(
        <TextInput
          key={field.name}
          id={field.name}
          label={field.label}
          type="url"
          value={getValueWithDefault(field.name, field.defaultValue)}
          error={field.skipBlurValidation ? undefined : errors?.[field.name]}
          placeholder={field.placeholder}
          onChange={(value) => handleChange(field.name, value)}
          onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          disabled={isDisabled}
        />,
        field.tooltip,
        isDisabled
      );
    case "textArea":
      return withTooltip(
        <TextArea
          key={field.name}
          id={field.name}
          label={field.label}
          value={getValueWithDefault(field.name, field.defaultValue)}
          placeholder={field.placeholder || ""}
          error={field.skipBlurValidation ? undefined : errors?.[field.name]}
          onChange={(value) => handleChange(field.name, value)}
          onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          disabled={isDisabled}
        />,
        field.tooltip,
        isDisabled
      );
    // TextEditor (unlike TextInput/Select/DateField) has no built-in `label`,
    // `error`, or `onBlur` support - see NewFieldModal's identical swap for
    // the same reason. A real, small capability loss vs. the old
    // FormInputRichTextEditor, accepted here since neither "textEditor" (no
    // live caller anywhere, not even in the admin field-type registry) nor
    // "richEditor" (reachable only via backend-configured custom fields, no
    // literal caller in the repo) has ever been seen exercising either.
    case "textEditor":
      return withTooltip(
        <div key={field.name}>
          {field.label && <p className="render-input-field-label">{field.label}</p>}
          <TextEditor
            textEditorValue={getValueWithDefault(field.name, field.defaultValue)}
            setTextEditorValue={(newValue) => handleChange(field.name, newValue)}
            placeholder={field.placeholder}
            disabled={isDisabled}
          />
        </div>,
        field.tooltip,
        isDisabled
      );

    case "richEditor":
      return withTooltip(
        <div key={field.name}>
          {field.label && <p className="render-input-field-label">{field.label}</p>}
          <TextEditor
            textEditorValue={getValueWithDefault(field.name, field.defaultValue)}
            setTextEditorValue={(newValue) => handleChange(field.name, newValue)}
            placeholder={field.placeholder}
            disabled={isDisabled}
          />
        </div>,
        field.tooltip,
        isDisabled
      );

    // "simplifyUser"/"company"/"market"/"singleSelect"/"multiSelect" all
    // migrated off FormInputSingleSelect/FormInputMultiSelect onto the
    // in-house Select's `renderOption`+`isMulti` (see Select's own
    // docstring). Select's `value`/option `value` must stay a plain id
    // (never the whole item) - `key`s and its `option.value === value`
    // identity checks need a stable primitive, and Users()/Companies()/
    // Markets() mint a fresh array of new object references on every
    // render, so an object-identity comparison would never match. Every
    // case below therefore maps `items` -> `{label, value: id, searchText}`
    // options at this call site, and reverses `onChange`'s id back into the
    // full item before calling `handleChange` - callers still receive the
    // whole object exactly like before.
    case "simplifyUser": {
      const users = Users();
      const renderUserOption = (option) => {
        const item = users?.find((u) => u._id === option.value);
        if (!item) return option.label;
        return <PersonOption name={item.name} email={item.email} />;
      };
      const userOptions = (users || []).map((u) => ({
        label: u.name,
        value: u._id,
        searchText: [u.name, u.email].filter(Boolean).join(" "),
      }));

      return withTooltip(
        field.allowMultiple ? (
          <Select
            key={field.name}
            label={field.label}
            required={evaluateRequiredCondition(field, formData, allFields)}
            options={userOptions}
            isMulti
            value={(getArrayValueWithDefault(field.name, field.defaultValue) || []).map((u) => u?._id)}
            onChange={(ids) => handleChange(field.name, (users || []).filter((u) => ids.includes(u._id)))}
            renderOption={renderUserOption}
            disabled={isDisabled}
            error={field.skipBlurValidation ? undefined : errors?.[field.name]}
            onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          />
        ) : (
          <Select
            key={field.name}
            label={field.label}
            required={evaluateRequiredCondition(field, formData, allFields)}
            options={userOptions}
            value={getValueWithDefault(field.name, field.defaultValue)?._id}
            onChange={(id) => handleChange(field.name, (users || []).find((u) => u._id === id) || null)}
            renderOption={renderUserOption}
            disabled={isDisabled}
            error={field.skipBlurValidation ? undefined : errors?.[field.name]}
            onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          />
        ),
        field.tooltip,
        isDisabled
      );
    }

    case "company": {
      const companies = Companies(showToast, context);
      // No custom row rendering here even before this migration - the old
      // renderItem was just `<p>{item.name}</p>`, no richer than Select's
      // own default plain-label row, so no `renderOption` is needed.
      const companyOptions = (companies || []).map((c) => ({ label: c.name, value: c._id }));

      return withTooltip(
        field.allowMultiple ? (
          <Select
            key={field.name}
            label={field.label}
            required={evaluateRequiredCondition(field, formData, allFields)}
            options={companyOptions}
            isMulti
            value={(getArrayValueWithDefault(field.name, field.defaultValue) || []).map((c) => c?._id)}
            onChange={(ids) => handleChange(field.name, (companies || []).filter((c) => ids.includes(c._id)))}
            disabled={isDisabled}
            error={field.skipBlurValidation ? undefined : errors?.[field.name]}
            onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          />
        ) : (
          <Select
            key={field.name}
            label={field.label}
            required={evaluateRequiredCondition(field, formData, allFields)}
            options={companyOptions}
            value={getValueWithDefault(field.name, field.defaultValue)?._id}
            onChange={(id) => handleChange(field.name, (companies || []).find((c) => c._id === id) || null)}
            disabled={isDisabled}
            error={field.skipBlurValidation ? undefined : errors?.[field.name]}
            onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          />
        ),
        field.tooltip,
        isDisabled
      );
    }

    case "market": {
      const markets = Markets();
      const marketOptions = (markets || []).map((m) => ({ label: m.marketName, value: m._id }));

      return withTooltip(
        field.allowMultiple ? (
          <Select
            key={field.name}
            label={field.label}
            required={evaluateRequiredCondition(field, formData, allFields)}
            options={marketOptions}
            isMulti
            value={(getArrayValueWithDefault(field.name, field.defaultValue) || []).map((m) => m?._id)}
            onChange={(ids) => handleChange(field.name, (markets || []).filter((m) => ids.includes(m._id)))}
            disabled={isDisabled}
            error={field.skipBlurValidation ? undefined : errors?.[field.name]}
            onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          />
        ) : (
          <Select
            key={field.name}
            label={field.label}
            required={evaluateRequiredCondition(field, formData, allFields)}
            options={marketOptions}
            value={getValueWithDefault(field.name, field.defaultValue)?._id}
            onChange={(id) => handleChange(field.name, (markets || []).find((m) => m._id === id) || null)}
            disabled={isDisabled}
            error={field.skipBlurValidation ? undefined : errors?.[field.name]}
            onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          />
        ),
        field.tooltip,
        isDisabled
      );
    }

    case "singleSelect": {
      // Roles has no unbounded "get everything" backend endpoint (unlike
      // every other referenceType here) - routes through the async,
      // real-search picker instead of the generic items-array path below,
      // which would otherwise truncate at whatever page the caller fetched.
      // See RoleAsyncSelect's own docstring.
      if (field.dynamicConfig?.referenceType === "roles") {
        return withTooltip(
          <RoleAsyncSelect
            key={field.name}
            label={field.label}
            value={getValueWithDefault(field.name, field.defaultValue)}
            onChange={(role) => handleChange(field.name, role)}
            disabled={isDisabled}
            error={field.skipBlurValidation ? undefined : errors?.[field.name]}
            onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
            excludeId={field.excludeId}
          />,
          field.tooltip,
          isDisabled
        );
      }

      const selectItems = field.items || [];
      const isObject = field.isObject || false;
      const valueSelector = field.valueSelector || "value";
      const valueDisplay = field.valueDisplay || valueSelector;
      const searchSelectors = Array.isArray(field.searchSelector)
        ? field.searchSelector
        : [field.searchSelector || "name"];

      const options = isObject
        ? selectItems.map((item) => ({
            label: item?.[valueDisplay],
            value: item?.[valueSelector],
            searchText: searchSelectors.map((s) => item?.[s]).filter(Boolean).join(" "),
          }))
        : selectItems.map((item) => ({ label: item, value: item }));

      const storedValue = getValueWithDefault(field.name, field.defaultValue);

      // See REFERENCE_TYPE_CREATE_ACTIONS' own comment above - only fires
      // for the 3 referenceTypes useResolveFormSchema.js always resolves
      // with isObject:true, so `created` (the entity-modal's resolved
      // record) is the same shape `selectItems` already holds and can be
      // handed straight to the same handleChange(field.name, <item>) path
      // a real selection uses, no extra mapping needed.
      const createAction = isObject ? REFERENCE_TYPE_CREATE_ACTIONS[field.dynamicConfig?.referenceType] : undefined;

      return withTooltip(
        <Select
          key={field.name}
          label={field.label}
          required={evaluateRequiredCondition(field, formData, allFields)}
          options={options}
          value={isObject ? storedValue?.[valueSelector] : storedValue}
          onChange={(value) => {
            if (!isObject) {
              handleChange(field.name, value);
              return;
            }
            handleChange(field.name, selectItems.find((item) => item?.[valueSelector] === value) ?? null);
          }}
          renderOption={
            field.renderItem
              ? (option) => {
                  const item = isObject ? selectItems.find((i) => i?.[valueSelector] === option.value) : option.value;
                  return item !== undefined ? field.renderItem(item) : option.label;
                }
              : undefined
          }
          creatable={Boolean(field.allowNew)}
          footerAction={
            createAction && !isDisabled
              ? {
                  label: createAction.label,
                  icon: Plus,
                  onClick: async () => {
                    const created = await openModal(createAction.modalTool);
                    const record = createAction.unwrap ? createAction.unwrap(created) : created;
                    if (record) handleChange(field.name, record);
                  },
                }
              : undefined
          }
          disabled={isDisabled}
          isLoading={Boolean(field.isLoading)}
          emptyMessage={field.emptyMessage}
          searchable={field.searchable}
          error={field.skipBlurValidation ? undefined : errors?.[field.name]}
          onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
        />,
        field.tooltip,
        isDisabled
      );
    }

    case "multiSelect": {
      if (field.dynamicConfig?.referenceType === "roles") {
        return withTooltip(
          <RoleAsyncSelect
            key={field.name}
            label={field.label}
            isMulti
            value={getArrayValueWithDefault(field.name, field.defaultValue)}
            onChange={(roles) => handleChange(field.name, roles)}
            disabled={isDisabled}
            error={field.skipBlurValidation ? undefined : errors?.[field.name]}
            onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
            excludeId={field.excludeId}
          />,
          field.tooltip,
          isDisabled
        );
      }

      const multiSelectItems = field.items || [];
      const isObject = field.isObject || false;
      const valueSelector = field.valueSelector || "value";
      const valueDisplay = field.valueDisplay || valueSelector;
      const searchSelectors = Array.isArray(field.searchSelector)
        ? field.searchSelector
        : [field.searchSelector || "name"];

      const options = isObject
        ? multiSelectItems.map((item) => ({
            label: item?.[valueDisplay],
            value: item?.[valueSelector],
            searchText: searchSelectors.map((s) => item?.[s]).filter(Boolean).join(" "),
            disabled: item?.disabled,
          }))
        : multiSelectItems.map((item) => ({ label: item, value: item }));

      const storedValues = getArrayValueWithDefault(field.name, field.defaultValue) || [];

      return withTooltip(
        <Select
          key={field.name}
          label={field.label}
          required={evaluateRequiredCondition(field, formData, allFields)}
          options={options}
          isMulti
          value={isObject ? storedValues.map((v) => v?.[valueSelector]) : storedValues}
          onChange={(values) => {
            if (!isObject) {
              handleChange(field.name, values);
              return;
            }
            handleChange(field.name, multiSelectItems.filter((item) => values.includes(item?.[valueSelector])));
          }}
          renderOption={
            field.renderItem
              ? (option) => {
                  const item = isObject
                    ? multiSelectItems.find((i) => i?.[valueSelector] === option.value)
                    : option.value;
                  return item !== undefined ? field.renderItem(item) : option.label;
                }
              : undefined
          }
          disabled={isDisabled}
          searchable={field.searchable}
          error={field.skipBlurValidation ? undefined : errors?.[field.name]}
          onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
        />,
        field.tooltip,
        isDisabled
      );
    }

    case "phoneNumber":
      return withTooltip(
        <PhoneInput
          key={field.name}
          label={field.label || "Phone number"}
          required={evaluateRequiredCondition(field, formData, allFields)}
          value={getValueWithDefault(field.name, field.defaultValue)}
          onChange={(value) => handleChange(field.name, value)}
          onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          error={field.skipBlurValidation ? undefined : errors?.[field.name]}
          disabled={isDisabled}
        />,
        field.tooltip,
        isDisabled
      );

    case "date":
      return withTooltip(
        <DateField
          key={field.name}
          label={field.label}
          value={getValueWithDefault(field.name, field.defaultValue)}
          onChange={(date) => handleChange(field.name, date)}
          onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          noPastDates={field.noPastDates}
          noFutureDates={field.noFutureDates}
          disabled={isDisabled}
          error={field.skipBlurValidation ? undefined : errors?.[field.name]}
          placeholder={field.placeholder}
        />,
        field.tooltip,
        isDisabled
      );

    case "dateAndTime":
      return withTooltip(
        <DateField
          key={field.name}
          label={field.label}
          showTime
          value={getValueWithDefault(field.name, field.defaultValue)}
          onChange={(date) => handleChange(field.name, date)}
          onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          noPastDates={field.noPastDates}
          noFutureDates={field.noFutureDates}
          disabled={isDisabled}
          error={field.skipBlurValidation ? undefined : errors?.[field.name]}
          placeholder={field.placeholder}
        />,
        field.tooltip,
        isDisabled
      );

    case "radioButton":
      return withTooltip(
        <RadioGroup
          key={field.name}
          title={field.title || field.label}
          options={field.options || field.items}
          onChange={(value) => handleChange(field.name, value)}
          onBlur={handleBlur ? () => handleBlur(field.name) : undefined}
          disabled={isDisabled}
          value={getValueWithDefault(field.name, field.defaultValue)}
          error={field.skipBlurValidation ? undefined : errors?.[field.name]}
          name={field.name}
        />,
        field.tooltip,
        isDisabled
      );

    case "checkboxContainer":
      return withTooltip(
        <Checkbox
          key={field.name}
          id={field.name}
          label={field.title || field.label}
          description={field.description}
          value={Boolean(formData[field.name])}
          onChange={(value) => handleChange(field.name, value)}
          disabled={isDisabled}
        />,
        field.tooltip,
        isDisabled
      );

    case "segmentedToggle":
      return withTooltip(
        <SegmentedToggle
          key={field.name}
          id={field.name}
          label={field.label}
          options={field.options || field.items}
          value={getValueWithDefault(field.name, field.defaultValue)}
          onChange={(value) => handleChange(field.name, value)}
          disabled={isDisabled}
        />,
        field.tooltip,
        isDisabled
      );

    // Migrated onto the already-existing _components/ui/LinkedItemsPicker
    // (its own docstring already says it "replaces the old-package
    // FormInputListManager") rather than building a new component - a
    // genuine, already-shipped equivalent under a different name.
    // `renderItem`/`renderSelectedItem`/`searchSelector` have no
    // LinkedItemsPicker equivalent (it always searches on `labelKey`, no
    // custom row rendering) and `disabled` isn't a prop it exposes at all -
    // real, accepted gaps since listManager has zero real callers anywhere
    // in the app to actually need them. `onCtaClick` only keeps its
    // string-URL-as-route-link behavior (`ctaHref`); a function `onCtaClick`
    // has no equivalent since LinkedItemsPicker's CTA is a plain `<Link>`,
    // not an onClick handler.
    case "listManager":
      const selectedResolved = (formData[field.name] || [])
        .map((sel) => {
          const selValue =
            typeof sel === "object" ? sel[field.valueSelector || "id"] : sel;
          return (field.items || []).find(
            (item) => item[field.valueSelector || "id"] === selValue
          );
        })
        .filter(Boolean);

      return withTooltip(
        <LinkedItemsPicker
          key={field.name}
          label={field.title || field.label}
          disclaimer={field.disclaimer}
          ctaLabel={field.ctaLabel}
          ctaHref={typeof field.onCtaClick === "string" ? field.onCtaClick : undefined}
          items={field.items || []}
          isLoading={Boolean(field.isLoading)}
          selected={selectedResolved}
          valueKey={field.valueSelector || "id"}
          labelKey={field.valueDisplay}
          onChange={(value) => handleChange(field.name, value)}
        />,
        field.tooltip,
        isDisabled
      );

    case "tieredPricing":
      return withTooltip(
        <>
          {pricingTiers && setPricingTiers && (
            <TieredPricingField
              key={field.name}
              pricingTiers={pricingTiers}
              setPricingTiers={setPricingTiers}
              disableAddTier={field.disableAddTier}
              disabled={isDisabled}
            />
          )}
        </>,
        field.tooltip,
        isDisabled
      );

    case "walletCreditsDynamic":
      return withTooltip(
        <>
          <div className="wallet-credits-field">
            <TextInput
              key={formData?.usageCompilations?._id}
              type="number"
              label={`${field.label} - ${formData?.usageCompilations?.title}`}
              value={formData?.walletCredits}
              onChange={(value) => {
                handleChange("walletCredits", value);
              }}
              onBlur={
                handleBlur ? () => handleBlur("walletCredits") : undefined
              }
              disabled={field?.disabled}
            />
          </div>
        </>,
        field.tooltip,
        isDisabled
      );

    case "button":
      // This case is handled in the modal component itself
      // Return null here as buttons are rendered separately
      return null;

    default:
      return null;
  }
};

export default renderInput;
